// Fulfillment photo upload orchestrator — Phase 3 (2026-10-03).
//
// Pulls the compress → sign → PUT → attach loop out of
// ProcedureFulfillmentsPanel so the multi-photo gallery and any future
// surface (admit note, encounter note) can share the same path.
//
// Returns the hydrated FulfillmentPhotoView from the attach step so the
// caller can append it to local state without a full refetch. Every step
// surfaces its error with enough context to render a toast — the UI
// decides whether to retry or roll back.

import type { FulfillmentPhotoView } from '@pamosi/shared';
import { compressPhoto, putPhotoToSignedUrl } from './photo';
import { proceduresApi } from './api';

export interface UploadFulfillmentPhotoArgs {
    serviceRequestId: string;
    fulfillmentId: string;
    file: File;
}

export async function uploadFulfillmentPhoto(
    args: UploadFulfillmentPhotoArgs,
): Promise<FulfillmentPhotoView> {
    // 1. compress — downscale to 1600px / q=0.82 JPEG (see lib/photo.ts).
    const compressed = await compressPhoto(args.file);

    // 2. sign — server allocates the photo row (pending) + a signed PUT URL.
    const sign = await proceduresApi.signPhotoUpload(
        args.serviceRequestId,
        args.fulfillmentId,
        { content_type: compressed.contentType },
    );

    // 3. PUT the compressed blob straight to Storage.
    await putPhotoToSignedUrl(sign.signed_url, compressed.blob);

    // 4. attach — flips the row to live and returns the hydrated view with a
    //    signed GET URL for the thumbnail.
    const res = await proceduresApi.attachPhoto(
        args.serviceRequestId,
        args.fulfillmentId,
        sign.photo_id,
    );
    return res.photo;
}
